import { Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPrompt, DialogPromptApi, DialogPromptSpeakNoGrade } from "../../api/DialogPromptApi";
import AppBarContext from '../../context/AppBarContext';
import { useSentenceChinesePinyin } from "../../hook/useSentenceChinesePinyin";
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPromptSpeakNoGradeFormProps {
    oldDialogPromptId: number;
    setNewDialogPrompt: React.Dispatch<React.SetStateAction<DialogPrompt | null>>;
    id: number;
    dialogId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    isHidden: boolean;
    yesResponse: string | null;
    imageSource: string | null;
    imageSourceUrl: string | null;
}

export const DialogPromptSpeakNoGradeForm: React.FC<DialogPromptSpeakNoGradeFormProps> = ({
    oldDialogPromptId, setNewDialogPrompt,
    id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, imageSource, imageSourceUrl }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const {
        text: teacherChinesePrompt,
        pinyin: teacherChinesePromptPinyin,
        pinyinList: teacherChinesePromptPinyinList,
        pinyinErrorText: teacherChinesePromptPinyinErrorText,
        setDialogPromptFields: setTeacherChinesePromptFields,
        clearDialogPromptFields: clearTeacherChinesePromptFields,
        clearErrorText: clearTeacherChinesePromptPinyinErrorText,
        handleTextChange: handleTeacherChinesePromptChange,
        handlePinyinChange: handleTeacherChinesePromptPinyinChange,
    } = useSentenceChinesePinyin({ history, location, setErrorMessage });

    const [studentResponsePrompt, setStudentResponsePrompt] = React.useState("<Answer/>");
    const [studentResponsePromptError, setStudentResponsePromptError] = React.useState(false);
    const [studentResponsePromptHelperText, setStudentResponsePromptHelperText] = React.useState('');
    const handleStudentResponsePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setStudentResponsePrompt(value);

        const matches = value.match(/<Answer\/>/g) || [];
        if (matches.length !== 1) {
            setStudentResponsePromptError(true);
            setStudentResponsePromptHelperText("Prompt must contain exactly one <Answer/>");
        } else {
            setStudentResponsePromptError(false);
            setStudentResponsePromptHelperText('');
        }
    };
    const setDialogPromptFields = React.useCallback((dialogPrompt: DialogPromptSpeakNoGrade) => {
        if (dialogPrompt.class_name == "DialogPromptSpeakNoGradeModel") {
            setTeacherChinesePromptFields(dialogPrompt.teacher_chinese_prompt, dialogPrompt.teacher_chinese_prompt_pinyin, dialogPrompt.teacher_chinese_prompt_sentence_id)
            setStudentResponsePrompt(dialogPrompt.student_response_prompt ? dialogPrompt.student_response_prompt : "")
        } else {
            clearTeacherChinesePromptFields()
            setStudentResponsePrompt("")
        }
    }, [setTeacherChinesePromptFields, clearTeacherChinesePromptFields, setStudentResponsePrompt])

    React.useEffect(() => {
        DialogPromptApi.getById(oldDialogPromptId)
            .then((response) => {
                const oldDialogPrompt = response.data as DialogPromptSpeakNoGrade;
                setDialogPromptFields(oldDialogPrompt);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        clearTeacherChinesePromptPinyinErrorText()
    }, [oldDialogPromptId, clearTeacherChinesePromptPinyinErrorText, history, location, setErrorMessage, setDialogPromptFields])


    React.useEffect(() => {

        const dialogPrompt = new DialogPromptSpeakNoGrade(
            id,
            dialogId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            isHidden,
            yesResponse as string | "ERROR",
            imageSource,
            imageSourceUrl,
            teacherChinesePrompt,
            null, // sentence_id is null
            teacherChinesePromptPinyinList,
            studentResponsePrompt,
        );
        setNewDialogPrompt(dialogPrompt);
    }, [id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, imageSource, imageSourceUrl,
        teacherChinesePrompt, teacherChinesePromptPinyinList, studentResponsePrompt, setNewDialogPrompt])

    return (
        <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>

            <TextField
                label="Teacher Chinese Prompt"
                value={teacherChinesePrompt}
                onChange={handleTeacherChinesePromptChange}
            />
            <TextField
                label="Teacher Chinese Prompt Pinyin"
                value={teacherChinesePromptPinyin}
                onChange={handleTeacherChinesePromptPinyinChange}
                error={teacherChinesePromptPinyinErrorText != null}
                helperText={teacherChinesePromptPinyinErrorText}
            />
            <div>
                <TextField
                    label="Student Chinese Prompt"
                    value={studentResponsePrompt ? studentResponsePrompt : "<Answer/>"}
                    onChange={handleStudentResponsePromptChange}
                    required
                    sx={{ width: '100%' }}
                    error={studentResponsePromptError}
                    helperText={studentResponsePromptHelperText}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Use &lt;Answer/&gt; to mark where the response is inserted.<br />
                    The prompt will be skipped and not displayed, if this field is only &lt;Answer/&gt;
                </Typography>
            </div>
        </Stack>
    )
}